/**
 * The play layer: mounts a game iframe, runs the bridge host, and owns
 * the run's score lifecycle (initials → submitScore → rank toast).
 *
 * One launch = one iframe. BACK tears the whole thing down — games keep
 * their own retry loops inside the frame, and every finished run fires
 * a fresh `gameover`, so multiple submissions per launch are expected.
 */

import { createCartridgeHost, type CartridgeHost } from '@arcraz/game-bridge';
import { recordRunTelemetry, submitScore } from '../api/client.js';
import { gameUrl, type CatalogEntry } from '../catalog.js';
import { track } from '../runtime/analytics.js';
import { getOrCreateDeviceId } from '../runtime/identity.js';
import { mountAdSlot } from './ad-slot.js';
import { runInitialsEntry } from './initials.js';
import { recordPlay } from './plays.js';
import { pushToast } from './toast.js';
import { getCabinetMuted, onCabinetMuteChange, setCabinetMuted } from './mute.js';

/** Same rhythm as the in-game (bridge) double-ESC. */
const EXIT_DOUBLE_TAP_MS = 1000;

function newSeed(): string {
  // Arcade-mode runs are non-deterministic in the remaster; the seed
  // column still gets a unique run tag (schema requires 1..128 chars).
  return `remaster-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface PlaySession {
  close(): void;
}

export function launchGame(entry: CatalogEntry, opts: { onClose: () => void }): PlaySession {
  const layer = document.getElementById('play-layer');
  const frameSlot = document.getElementById('play-frame-slot');
  const titleEl = document.getElementById('play-title');
  const scoreEl = document.getElementById('play-score');
  const backBtn = document.getElementById('play-back');
  if (
    !(layer instanceof HTMLElement) ||
    !(frameSlot instanceof HTMLElement) ||
    !(titleEl instanceof HTMLElement) ||
    !(scoreEl instanceof HTMLElement) ||
    !(backBtn instanceof HTMLButtonElement)
  ) {
    throw new Error('play layer DOM missing — index.html is broken');
  }
  // Hoisted `close()` below can't see the instanceof narrowing — alias
  // the proven nodes so the compiler carries the types into the closure.
  const layerEl: HTMLElement = layer;
  const backBtnEl: HTMLButtonElement = backBtn;

  titleEl.textContent = entry.title;
  titleEl.style.color = entry.accent;
  scoreEl.textContent = entry.scoreless ? '' : '000000';

  const iframe = document.createElement('iframe');
  iframe.className = 'play-frame';
  iframe.src = gameUrl(entry);
  // pointer-lock is load-bearing: bunker-84 is unplayable without it.
  iframe.allow = 'autoplay; fullscreen; pointer-lock';
  iframe.title = entry.title;
  frameSlot.appendChild(iframe);

  // House-ad slot in the top chrome (sc-6959). No-op for every cartridge
  // except stray-pixel; ticks on VISIBLE time via rAF (throttled in hidden
  // tabs), reveals after 30s, rotates on its own. Game never sees it.
  const chromeEl = layerEl.querySelector('.play-chrome') as HTMLElement | null;
  const adSlot = chromeEl ? mountAdSlot(chromeEl, entry.id) : null;
  let adRaf = 0;
  let adLast = performance.now();
  const adTick = (now: number): void => {
    const dt = now - adLast;
    adLast = now;
    adSlot?.tick(dt);
    adRaf = requestAnimationFrame(adTick);
  };
  if (adSlot) adRaf = requestAnimationFrame(adTick);

  const seed = newSeed();
  const startedAt = performance.now();
  let submitting = false;
  let playRecorded = false;
  // Quit telemetry state (sc-6928): exactly one run_telemetry row per
  // run, however it ends — gameover, double-ESC quit, or tab abandon.
  let runEnded = false;
  let lastScore = 0;
  let quitSnapshot: { score?: number; stats?: Record<string, string | number | boolean> } | null = null;

  const host: CartridgeHost = createCartridgeHost(iframe, {
    onReady: () => {
      host.init({ muted: getCabinetMuted(), seed });
      track('cartridge_started', { cartridgeId: entry.id, remastered: true });
    },
    onStarted: () => {
      // The play (and its free-play meter tick) burns HERE — the player
      // dismissed the start overlay and committed to a run. Launching
      // and backing out from the start screen costs nothing. Guarded to
      // once per launch: in-frame retries re-fire `started`, and a
      // retry is not a new coin.
      if (playRecorded) return;
      playRecorded = true;
      recordPlay(entry.id);
    },
    onScore: (score) => {
      lastScore = score;
      if (!entry.scoreless) {
        scoreEl.textContent = String(Math.floor(score)).padStart(6, '0');
      }
    },
    onGameOver: (result) => {
      void handleGameOver(result.score, result.won, result.stats);
    },
    onExit: (snapshot) => {
      // Hold the snapshot; the row is only written if the eject
      // actually completes (the confirm gate can cancel it).
      quitSnapshot = snapshot ?? null;
      requestExit();
    },
    onMuted: (muted) => setCabinetMuted(muted),
    onSnapshot: (snapshot) => {
      // Reply to the requestSnapshot() fired in requestExit(). The
      // in-game double-ESC snapshot (onExit) wins if both arrive.
      if (!quitSnapshot) quitSnapshot = snapshot;
    },
  });

  const offMute = onCabinetMuteChange((muted) => host.setMuted(muted));

  async function handleGameOver(
    score: number,
    won: boolean,
    stats?: Record<string, string | number | boolean>,
  ): Promise<void> {
    const durationMs = Math.round(performance.now() - startedAt);
    runEnded = true;
    // Per-game RunStats land in OUR database (sc-6919), not a
    // third-party analytics bucket. Fire-and-forget by contract.
    recordRunTelemetry({
      cartridgeId: entry.id,
      cartridgeVersion: entry.validatorVersion,
      deviceId: getOrCreateDeviceId(),
      score: Math.max(0, Math.floor(score)),
      won,
      durationMs,
      stats: { ...(stats ?? {}), endedBy: 'gameover' },
    });
    track('cartridge_run_ended', {
      cartridgeId: entry.id,
      score,
      won,
      durationMs,
    });
    if (entry.scoreless || score <= 0 || submitting) return;
    submitting = true;
    try {
      const initials = await runInitialsEntry(score);
      if (initials === null) return; // player skipped the board
      const response = await submitScore({
        cartridgeId: entry.id,
        cartridgeVersion: entry.validatorVersion,
        score: Math.floor(score),
        initials,
        seed,
        inputFrames: [],
        deviceId: getOrCreateDeviceId(),
      });
      pushToast(`${entry.title} — RANK #${response.rank}`, 'success');
      track('score_submitted', {
        cartridgeId: entry.id,
        score,
        rank: response.rank,
      });
    } catch {
      pushToast('SCORE LOST IN TRANSIT — THE SCOREBOARD WILL RISE AGAIN', 'error');
    } finally {
      submitting = false;
    }
  }

  /**
   * The quit/abandon telemetry row (sc-6928). Only fires for a run
   * that STARTED and never reached gameover; a start-screen back-out
   * costs nothing and reports nothing.
   */
  function recordUnfinishedRun(endedBy: 'quit' | 'abandoned'): void {
    if (!playRecorded || runEnded) return;
    runEnded = true;
    recordRunTelemetry({
      cartridgeId: entry.id,
      cartridgeVersion: entry.validatorVersion,
      deviceId: getOrCreateDeviceId(),
      score: Math.max(0, Math.floor(quitSnapshot?.score ?? lastScore)),
      won: false,
      durationMs: Math.round(performance.now() - startedAt),
      stats: { ...(quitSnapshot?.stats ?? {}), endedBy },
    });
  }
  const onPageHide = (): void => recordUnfinishedRun('abandoned');
  window.addEventListener('pagehide', onPageHide);

  let closed = false;
  function close(): void {
    // Guarded: the bridge onExit and the parent ESC handler can race
    // (whichever window held focus got the keys) — close once.
    if (closed) return;
    closed = true;
    recordUnfinishedRun('quit');
    if (adRaf) cancelAnimationFrame(adRaf);
    adSlot?.dispose();
    window.removeEventListener('pagehide', onPageHide);
    confirmEl?.remove();
    confirmEl = null;
    offMute();
    window.removeEventListener('keydown', onParentKeydown);
    host.dispose();
    iframe.remove();
    layerEl.hidden = true;
    document.body.dataset.mode = 'attract';
    backBtnEl.removeEventListener('click', requestExit);
    track('cartridge_exited', {
      cartridgeId: entry.id,
      durationMs: Math.round(performance.now() - startedAt),
    });
    opts.onClose();
  }

  // Exit gate: once a run has started (token spent, score in flight),
  // every exit path — in-game double-ESC, parent double-ESC, BACK — has
  // to clear a confirmation. Before that, eject instantly: nothing is
  // at stake on a loading/start screen. A second exit request while the
  // dialog is up counts as confirmation (double-ESC twice ejects).
  let confirmEl: HTMLElement | null = null;
  function requestExit(): void {
    if (closed) return;
    // Cabinet-side exits (BACK, parent ESC) never produce an in-game
    // `exit` message, so the quit row would ship without stats (the
    // stray-pixel gap, sc-6928). Ask the game now — the reply lands in
    // onSnapshot long before the confirm gate resolves. Best-effort: a
    // near-instant double-ESC eject may still beat the round-trip.
    if (!quitSnapshot) host.requestSnapshot();
    if (!playRecorded) {
      close();
      return;
    }
    if (confirmEl) {
      close();
      return;
    }
    showExitConfirm();
  }

  function showExitConfirm(): void {
    host.pause();
    const make = (tag: string, cls: string, text?: string): HTMLElement => {
      const el = document.createElement(tag);
      el.className = cls;
      if (text !== undefined) el.textContent = text;
      return el;
    };
    confirmEl = make('div', 'exit-confirm');
    const box = make('div', 'exit-confirm__box');
    box.append(
      make('div', 'exit-confirm__title', 'EJECT CARTRIDGE?'),
      make('div', 'exit-confirm__sub', 'run in progress — the score dies with it')
    );
    const actions = make('div', 'exit-confirm__actions');
    const noBtn = make('button', 'exit-confirm__no', 'KEEP PLAYING') as HTMLButtonElement;
    const yesBtn = make('button', 'exit-confirm__yes', 'EJECT · ESC') as HTMLButtonElement;
    noBtn.type = 'button';
    yesBtn.type = 'button';
    noBtn.addEventListener('click', hideExitConfirm);
    yesBtn.addEventListener('click', () => close());
    actions.append(noBtn, yesBtn);
    box.appendChild(actions);
    confirmEl.appendChild(box);
    layerEl.appendChild(confirmEl);
    noBtn.focus();
  }

  function hideExitConfirm(): void {
    confirmEl?.remove();
    confirmEl = null;
    host.resume();
    iframe.focus();
  }
  backBtn.addEventListener('click', requestExit);

  // Parent-side double-ESC. The bridge installs the same hatch inside
  // the game's window, but keyboard focus doesn't reach the iframe
  // until the player clicks into it — on the loading/start screen every
  // keypress lands HERE. Without this, a player who launched by mistake
  // can't leave without starting a run.
  let lastEscAt = 0;
  function onParentKeydown(e: KeyboardEvent): void {
    if (confirmEl) {
      // Dialog open: ESC ejects, Enter/Tab drive the buttons natively,
      // anything else reads as "keep playing".
      if (e.code === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.code !== 'Enter' && e.code !== 'Tab' && e.code !== 'Space') {
        hideExitConfirm();
      }
      return;
    }
    if (e.code !== 'Escape') return;
    const now = Date.now();
    if (now - lastEscAt < EXIT_DOUBLE_TAP_MS) {
      lastEscAt = 0;
      requestExit();
    } else {
      lastEscAt = now;
    }
  }
  window.addEventListener('keydown', onParentKeydown);

  layer.hidden = false;
  document.body.dataset.mode = 'playing';
  // Focus once the document inside actually exists — focusing the empty
  // frame pre-load doesn't stick, which is how ESC ended up dead on
  // start screens (keys stayed in the parent, see handler above).
  // Focus the contentWindow too, not just the element: an unfocused
  // cross-origin frame swallows its FIRST click as a focus transfer, so
  // the player's first tap on the start screen did nothing (they had to
  // click twice). Focusing the window on load delivers that first click.
  iframe.addEventListener('load', () => {
    iframe.focus();
    iframe.contentWindow?.focus();
  });
  iframe.focus();

  return { close };
}
